import type { RenderRecipe, LightRecipe } from './types'

/** Decode a model file, honoring the BOM (files are UTF-16LE or UTF-8). */
export function decodeModelBytes(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  if (bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder('utf-16le').decode(bytes.subarray(2))
  }
  if (bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder('utf-16be').decode(bytes.subarray(2))
  }
  // TextDecoder('utf-8') strips a leading UTF-8 BOM by default.
  return new TextDecoder('utf-8').decode(bytes)
}

const normalizeName = (s: string | null | undefined) => (s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')

/**
 * Resolve the wrapper's cover *geometry* name to the COLLADA *node* name(s) that
 * instance that geometry.
 *
 * The wrapper identifies the cover by geometry name ("Object003Mesh.001"), but
 * ColladaLoader drops geometry names — it names each object it builds after the
 * <node> that instances the geometry ("Object003_001"). On most of our models the two
 * are one normalize away from each other, so a string heuristic got by. On the page
 * ranges whose model was authored by duplicating the book (hardcover 142–180 and
 * 360–390, both trim sizes) the cover material is bound to the ".001" duplicate, and
 * "Object003Mesh.001" doesn't reduce to "Object003_001" by any rule worth trusting —
 * the match failed, no material got the skin, and the book rendered blank.
 *
 * Following the geometry id through <instance_geometry> is exact, so do that instead.
 * Returns [] if the geometry isn't in the scene graph (buildModel then falls back).
 */
export function resolveCoverNodeNames(colladaString: string, coverMeshName: string | null): string[] {
  const target = normalizeName(coverMeshName)
  if (!target) return []

  let doc: Document
  try {
    doc = new DOMParser().parseFromString(colladaString, 'application/xml')
  } catch {
    return []
  }
  if (doc.querySelector('parsererror')) return []

  // 1. Cover geometry name -> geometry id ("Object003Mesh.001" -> "Object003Mesh_001-mesh").
  const ids = new Set<string>()
  for (const geom of Array.from(doc.getElementsByTagName('geometry'))) {
    const id = geom.getAttribute('id')
    if (!id) continue
    // Some exporters omit @name and only carry the name in the id, with a -mesh suffix.
    if (normalizeName(geom.getAttribute('name')) === target || normalizeName(id.replace(/-mesh$/, '')) === target) {
      ids.add(id)
    }
  }
  if (ids.size === 0) return []

  // 2. Geometry id -> the <node> that instances it (that node names the built object).
  const names: string[] = []
  for (const inst of Array.from(doc.getElementsByTagName('instance_geometry'))) {
    const url = inst.getAttribute('url') ?? ''
    if (!url.startsWith('#') || !ids.has(url.slice(1))) continue
    let el: Element | null = inst.parentElement
    while (el && el.localName !== 'node') el = el.parentElement
    const name = el?.getAttribute('name') || el?.getAttribute('id') || inst.getAttribute('name')
    if (name) names.push(name)
  }
  return Array.from(new Set(names))
}

const num = (el: Element, name: string, fallback = 0): number => {
  const v = el.getAttribute(name)
  const n = v == null ? NaN : Number(v)
  return Number.isFinite(n) ? n : fallback
}

/**
 * Parse a Chili <threeDModel> wrapper into a RenderRecipe + the embedded COLLADA
 * string. Throws a clear Error on malformed/mojibake input rather than silently
 * yielding an empty scene.
 */
export function parseWrapper(buffer: ArrayBuffer): RenderRecipe {
  const text = decodeModelBytes(buffer)
  const doc = new DOMParser().parseFromString(text, 'application/xml')

  // DOMParser does not throw on malformed XML — it injects <parsererror>.
  const parseError = doc.querySelector('parsererror')
  if (parseError) {
    throw new Error(`Model XML failed to parse: ${parseError.textContent?.trim().slice(0, 200)}`)
  }

  const root = doc.documentElement
  if (!root || root.nodeName !== 'threeDModel') {
    throw new Error(`Unexpected root element <${root?.nodeName ?? 'null'}> (expected <threeDModel>)`)
  }

  // --- Lights: first directional light ---
  let light: LightRecipe | null = null
  const lightEl = Array.from(doc.querySelectorAll('lights > item')).find(
    (el) => el.getAttribute('type') === 'directional',
  )
  if (lightEl) {
    light = {
      direction: [num(lightEl, 'x'), num(lightEl, 'y'), num(lightEl, 'z', 1)],
      color: num(lightEl, 'color', 0xffffff),
      ambient: num(lightEl, 'ambient', 0.1),
      diffuse: num(lightEl, 'diffuse', 0.8),
      specular: num(lightEl, 'specular', 0.2),
      attachToCamera: lightEl.getAttribute('attachToCamera') === 'true',
    }
  }

  // --- Materials: the single document ("Cover 1") material ---
  const docMat = Array.from(doc.querySelectorAll('materials > item')).find(
    (el) => el.getAttribute('materialType') === 'document',
  )
  if (!docMat) {
    throw new Error('No document material ("Cover 1") found in wrapper')
  }
  const docMatId = docMat.getAttribute('id')
  const coverMaterialName = docMat.getAttribute('name')

  // --- Geometry: the item bound to the document material is the cover mesh ---
  const coverGeom = Array.from(doc.querySelectorAll('geometry > item')).find(
    (el) => el.getAttribute('materialID') === docMatId,
  )
  const coverMeshName = coverGeom?.getAttribute('name') ?? null
  if (!coverMeshName) {
    throw new Error(`No geometry item references the document material (${docMatId})`)
  }

  // --- Embedded COLLADA (DOMParser already un-escaped the entities) ---
  const modelData = doc.querySelector('modelData')?.textContent?.trim()
  if (!modelData || !modelData.includes('<COLLADA')) {
    throw new Error('Missing or invalid <modelData> (embedded COLLADA)')
  }

  return {
    backgroundColor: num(root, 'backgroundColor', 0xffffff),
    cameraRadius: num(root, 'initialCameraRadius', 650),
    cameraTarget: [
      num(root, 'initialCameraTargetX'),
      num(root, 'initialCameraTargetY'),
      num(root, 'initialCameraTargetZ'),
    ],
    scale: num(root, 'scale', 1),
    rotation: [
      num(root, 'modelRotationX'),
      num(root, 'modelRotationY'),
      num(root, 'modelRotationZ'),
    ],
    light,
    coverMeshName,
    coverNodeNames: resolveCoverNodeNames(modelData, coverMeshName),
    coverMaterialName,
    colladaString: modelData,
  }
}

/** Convert a packed 0xRRGGBB integer to a normalized [r,g,b] triple. */
export function unpackColor(packed: number): [number, number, number] {
  return [((packed >> 16) & 0xff) / 255, ((packed >> 8) & 0xff) / 255, (packed & 0xff) / 255]
}
